import { ECOMMERCE_CHANGE_SUBTYPES } from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/constants";
import EcommerceChangesPeriod from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceChangesPeriod";
import EcommerceChangesTrend from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceChangesTrend";
import type EcommerceSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";
import type {
    EcommerceBucketTransitionModel,
    EcommerceChangeFilterModel,
    EcommerceChangeMetric,
} from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";

import { ECOMMERCE_CHANGE_DIRECTION_TO_API, ECOMMERCE_CHANGE_PERIOD_TO_API } from "./ecommerceChangeApiMaps";

/** Narrows partial API/form `change` to a complete in-memory change model (shared by template + equality). */
export function isCompleteEcommerceChange(
    change: Partial<EcommerceChangeFilterModel> | undefined,
): change is EcommerceChangeFilterModel {
    return change != null && typeof change.metric !== "undefined" && change.direction != null && change.period != null;
}

/** Narrows partial `bucketTransition` for reached-type ecommerce signals (mirrors change guard pattern). */
export function isCompleteEcommerceBucketTransition(
    bucketTransition: Partial<EcommerceBucketTransitionModel> | undefined,
): bucketTransition is EcommerceBucketTransitionModel {
    return (
        bucketTransition != null &&
        typeof bucketTransition.metric !== "undefined" &&
        typeof bucketTransition.bucket === "string"
    );
}

export function normalizeDirectionForForm(value: string | undefined): EcommerceChangesTrend | undefined {
    if (typeof value === "undefined") {
        return undefined;
    }

    const normalized = value.toLowerCase();

    return normalized === EcommerceChangesTrend.Declined ? EcommerceChangesTrend.Declined : EcommerceChangesTrend.Grew;
}

export function normalizePeriodForForm(value: string | undefined): EcommerceChangesPeriod | undefined {
    if (typeof value === "undefined") {
        return undefined;
    }

    const normalized = value.toLowerCase();

    if (normalized === EcommerceChangesPeriod.QuoterOverQuoter) {
        return EcommerceChangesPeriod.QuoterOverQuoter;
    }

    if (normalized === EcommerceChangesPeriod.YearOverYear) {
        return EcommerceChangesPeriod.YearOverYear;
    }

    return EcommerceChangesPeriod.MonthOverMonth;
}

export function coerceEcommerceFiltersForForm(
    signalSubType: string | undefined,
    filters: Readonly<Partial<EcommerceSignalFilters>>,
): Partial<EcommerceSignalFilters> {
    const subType = signalSubType ?? "";
    const change = filters.change;

    if (change && ECOMMERCE_CHANGE_SUBTYPES.has(subType)) {
        return {
            change: {
                metric: change.metric,
                direction:
                    change.direction === undefined || change.direction === null
                        ? undefined
                        : normalizeDirectionForForm(change.direction as string) ?? EcommerceChangesTrend.Grew,
                period:
                    change.period === undefined || change.period === null
                        ? undefined
                        : normalizePeriodForForm(change.period as string) ?? EcommerceChangesPeriod.MonthOverMonth,
            },
        };
    }

    const bucketTransition = filters.bucketTransition;

    if (bucketTransition) {
        return {
            bucketTransition: {
                metric: bucketTransition.metric,
                bucket: bucketTransition.bucket,
            },
        };
    }

    return { ...filters };
}

export function ecommerceChangeToApiPayload(change: EcommerceChangeFilterModel) {
    return {
        metric: change.metric,
        direction: ECOMMERCE_CHANGE_DIRECTION_TO_API[change.direction],
        period: ECOMMERCE_CHANGE_PERIOD_TO_API[change.period],
    };
}

function normalizeEcommerceMetricForEquality(metric: string): EcommerceChangeMetric | null {
    const compact = metric.replace(/\s+/g, "").toLowerCase();

    if (compact === "onlinerevenue") {
        return "OnlineRevenue";
    }

    if (compact === "transactionvolume") {
        return "TransactionVolume";
    }

    return null;
}

function tryBuildCompleteEcommerceChange(
    change: Partial<EcommerceChangeFilterModel> | undefined,
): EcommerceChangeFilterModel | null {
    if (!isCompleteEcommerceChange(change)) {
        return null;
    }

    const metric =
        normalizeEcommerceMetricForEquality(change.metric) ??
        (change.metric === "OnlineRevenue" || change.metric === "TransactionVolume" ? change.metric : null);
    const direction = normalizeDirectionForForm(change.direction);
    const period = normalizePeriodForForm(change.period);

    if (metric === null || !direction || !period) {
        return null;
    }

    return { metric, direction, period };
}

export function normalizeEcommerceFiltersForEquality(
    filters: Partial<EcommerceSignalFilters>,
): Record<string, unknown> | null {
    const coercedChange = tryBuildCompleteEcommerceChange(filters.change);

    if (coercedChange != null) {
        return {
            change: ecommerceChangeToApiPayload(coercedChange),
        };
    }

    const bucketTransition = filters.bucketTransition;

    if (bucketTransition?.metric && bucketTransition.bucket) {
        const metric =
            normalizeEcommerceMetricForEquality(String(bucketTransition.metric)) ??
            (bucketTransition.metric === "OnlineRevenue" || bucketTransition.metric === "TransactionVolume"
                ? bucketTransition.metric
                : null);

        if (metric == null) {
            return null;
        }

        return {
            bucketTransition: {
                metric,
                bucket: bucketTransition.bucket,
            },
        };
    }

    return null;
}
